import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Home,
  Gamepad2,
  Users,
  Ticket,
  Send,
  CreditCard,
  Wallet,
  Settings,
  LogOut,
  Menu,
  ShieldAlert,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const NAV = [
  { to: "/vendor/dashboard", label: "Overview", icon: Home, testid: "nav-overview" },
  { to: "/vendor/campaigns", label: "Campaigns", icon: Gamepad2, testid: "nav-campaigns" },
  { to: "/vendor/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/vendor/verify", label: "Verify Coupon", icon: Ticket, testid: "nav-verify" },
  { to: "/vendor/retargeting", label: "Retargeting", icon: Send, testid: "nav-retargeting" },
  { to: "/vendor/wallet", label: "Wallet", icon: Wallet, testid: "nav-wallet" },
  { to: "/vendor/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/vendor/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const STATUS_CLASS = {
  pending: "ag-pill ag-pill--pending",
  active: "ag-pill ag-pill--active",
  suspended: "ag-pill ag-pill--suspended",
};

export default function VendorLayout() {
  const { vendor, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (vendor?.name || "V")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onLogout = () => {
    logout();
    navigate("/vendor/login");
  };

  const status = (vendor?.status || "active").toLowerCase();
  const businessName = (vendor?.business || "VENDOR").toUpperCase();
  const businessShort = businessName.length > 18 ? `${businessName.slice(0, 18)}…` : businessName;

  const sidebarContent = (
    <div className="flex h-full flex-col ag-sidebar">
      <Link to="/vendor/dashboard" className="px-5 pt-5 pb-4 flex items-center gap-2 min-w-0 border-b-2 border-black/90">
        <Logo size={128} light />
      </Link>
      <nav className="px-3 py-4 flex-1 space-y-1.5" aria-label="Vendor navigation">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) => `ag-nav-item ${isActive ? "ag-nav-item--active" : ""}`}
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t-2 border-black/90 bg-white/60">
        <div className="flex items-center gap-3 px-1 py-1">
          <Avatar className="h-9 w-9 border-2 border-black">
            <AvatarFallback className="bg-[var(--graffiti-yellow)] text-black text-xs font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-black truncate" data-testid="vendor-name">
              {vendor?.name}
            </p>
            <span
              className={`mt-1 inline-flex ${STATUS_CLASS[status] || "ag-pill ag-pill--neutral"}`}
              data-testid="vendor-status-badge"
            >
              {status}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onLogout}
            aria-label="Sign out"
            data-testid="logout-button"
            className="hover:bg-black hover:text-[var(--graffiti-yellow)] border-2 border-transparent hover:border-black"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app-light min-h-screen">
      <Toaster richColors position="top-center" />
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 z-40">
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      <div className={`lg:hidden fixed inset-0 z-40 ${open ? "" : "pointer-events-none"}`}>
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpen(false)}
        />
        <aside
          className={`absolute inset-y-0 left-0 w-72 transition-transform ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {sidebarContent}
        </aside>
      </div>

      {/* Top bar - mobile (logo on left, menu on right per brand spec §10) */}
      <header className="lg:hidden sticky top-0 z-30 ag-topbar">
        <div className="flex items-center gap-3 px-4 h-14">
          <Link to="/vendor/dashboard" aria-label="City DOOH home" className="inline-flex shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md">
            <Logo size={104} light />
          </Link>
          <span className="font-display text-sm text-black truncate flex-1 min-w-0">{businessShort}</span>
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label="Open menu" className="shrink-0">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <main className="lg:pl-64">
        <div className="max-w-[1400px] px-4 md:px-6 lg:px-8 py-6">
          {vendor?.status === "pending" && (
            <div className="mb-5 rounded-xl border-2 border-black bg-[var(--graffiti-yellow)] p-4 flex items-start gap-3 shadow-[5px_5px_0_#0a0a0a]" data-testid="vendor-pending-banner">
              <ShieldAlert className="h-5 w-5 text-black mt-0.5" />
              <div className="text-sm text-black">
                <p className="font-bold uppercase tracking-wide">Account pending activation</p>
                <p className="opacity-80">Our team will contact you shortly. Campaign creation is disabled until your subscription is activated.</p>
              </div>
            </div>
          )}
          {vendor?.status === "suspended" && (
            <div className="mb-4 rounded-xl border-2 border-black bg-[#fbd5d5] px-4 py-3 flex items-start gap-3 shadow-[5px_5px_0_#0a0a0a]" data-testid="suspended-banner">
              <ShieldAlert className="h-5 w-5 text-[#8b1212] mt-0.5" />
              <div className="text-sm">
                <p className="font-bold uppercase tracking-wide text-[#8b1212]">Account suspended</p>
                <p className="text-[#8b1212]/80">Please contact support to resolve.</p>
              </div>
            </div>
          )}
          <EmailVerifyBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Yellow "Please verify your email" banner shown until vendor.email_verified
// becomes true. Houses the resend + OTP-entry interactions inline.
// ----------------------------------------------------------------------------
function EmailVerifyBanner() {
  const { vendor, setVendor } = useAuth();
  const [open, setOpen] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);

  if (!vendor || vendor.email_verified) return null;

  const send = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      const r = await import("@/lib/api").then((m) => m.api.sendVerifyOtp());
      const cd = Number(r?.data?.cooldown_seconds) || 60;
      setCooldown(cd);
      setOpen(true);
      // Explicit confirmation — the banner CTA otherwise just opens the
      // modal silently. A toast makes it unambiguous that a mail was
      // actually dispatched (and that it's from the RIGHT address).
      toast.success(`Verification code sent to ${vendor.email}.`);
    } catch (e) {
      const data = e?.response?.data?.detail;
      if (data?.retry_after) setCooldown(Number(data.retry_after));
      setOpen(true);
      // Surface the server's actual reason (rate-limit, provider down,
      // invalid address). Falls back to a friendly generic when the
      // API didn't include a message.
      const msg = typeof data === "string" ? data : data?.message
        || "Couldn't send the verification code. Try again in a moment.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="mb-4 rounded-xl border-2 border-black bg-[var(--graffiti-yellow)] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 shadow-[5px_5px_0_#0a0a0a]" data-testid="verify-email-banner">
        <ShieldAlert className="h-5 w-5 text-black shrink-0" />
        <div className="text-sm flex-1 text-black">
          <p className="font-bold uppercase tracking-wide">Verify your email to unlock campaigns</p>
          <p className="opacity-80">
            We sent a 6-digit code to <span className="font-bold">{vendor.email}</span>. Once you verify, you can publish campaigns and reach customers.
          </p>
        </div>
        <button onClick={send} disabled={sending} className="ag-btn-ghost" data-testid="verify-email-cta">
          {sending ? "Sending…" : "Verify now"}
        </button>
      </div>
      <VerifyEmailModalLazy
        open={open}
        onOpenChange={setOpen}
        email={vendor.email}
        cooldown={cooldown}
        onVerified={(updatedVendor) => {
          if (updatedVendor) setVendor?.(updatedVendor);
          setOpen(false);
        }}
        onResend={send}
      />
    </>
  );
}

function VerifyEmailModalLazy({ open, onOpenChange, email, cooldown, onVerified, onResend }) {
  const [Modal, setModal] = useState(null);
  React.useEffect(() => {
    if (open && !Modal) {
      import("@/components/OtpModal").then((m) => setModal(() => m.OtpModal));
    }
  }, [open, Modal]);
  if (!Modal) return null;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      email={email}
      title="Verify your email"
      description={`Enter the 6-digit code we sent to ${email}.`}
      initialCooldown={cooldown}
      testIdPrefix="vendor-verify"
      onVerify={async (code) => {
        try {
          const r = await import("@/lib/api").then((m) => m.api.verifyEmailOtp(code));
          onVerified?.(r?.data?.vendor);
          toast.success("Email verified — you can now publish campaigns.");
        } catch (e) {
          const det = e?.response?.data?.detail;
          const msg = typeof det === "string" ? det : det?.message
            || "That code didn't work. Please try again or resend.";
          toast.error(msg);
          throw e; // preserve OtpModal's own inline-error handling
        }
      }}
      onResend={async () => {
        try {
          const r = await import("@/lib/api").then((m) => m.api.sendVerifyOtp());
          toast.success("A fresh code is on its way.");
          return r?.data || {};
        } catch (e) {
          const det = e?.response?.data?.detail;
          const msg = typeof det === "string" ? det : det?.message
            || "Couldn't resend the code. Please try again.";
          toast.error(msg);
          return det && typeof det === "object" ? det : {};
        }
      }}
    />
  );
}
